const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

class ApiService {
  constructor() {
    this.baseUrl = API_BASE_URL;
    // In-flight GET deduplication: concurrent identical GETs share one promise
    this.inflightGets = new Map();
  }

  async request(endpoint, options = {}) {
    const method = (options.method || 'GET').toUpperCase();
    if (method !== 'GET') {
      return this.executeRequest(endpoint, options);
    }

    const existing = this.inflightGets.get(endpoint);
    if (existing) {
      return existing;
    }

    const promise = this.executeRequest(endpoint, options).finally(() => {
      this.inflightGets.delete(endpoint);
    });
    this.inflightGets.set(endpoint, promise);
    return promise;
  }

  async executeRequest(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add Firebase auth token if available
    try {
      const { auth } = await import('../config/firebase');
      const user = auth?.currentUser;
      if (user) {
        const token = await user.getIdToken();
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      // Firebase may not be initialized yet for public routes - that's OK
      console.debug('Firebase auth not available:', error.message);
    }

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        const error = new Error(data.error || 'An error occurred');
        error.status = response.status;
        throw error;
      }

      return data;
    } catch (error) {
      // Suppress logging for expected 404 errors (e.g., places without photos)
      const isExpected404 = error.status === 404 && (
        endpoint.includes('/photo') ||
        error.message?.includes('No photo available')
      );
      if (!isExpected404) {
        console.error('API request failed:', error);
      }
      throw error;
    }
  }

  // Auth endpoints
  async createUser(userData) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async getUser(userId) {
    return this.request(`/users/${userId}`);
  }

  async getUserByUsername(username) {
    return this.request(`/users/username/${username}`);
  }

  async updateUser(userId, updates) {
    // Note: userId param kept for backwards compatibility but not used in URL
    // The backend uses the authenticated user's ID from the token
    return this.request('/users/me', {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async checkUsernameAvailability(username) {
    return this.request(`/users/check-username/${username}`);
  }

  async checkEmailExists(email) {
    return this.request(`/users/check-email/${encodeURIComponent(email)}`);
  }

  // List endpoints
  async getLists(userId, options = {}) {
    if (!userId) {
      throw new Error('userId is required to get lists');
    }
    const { includePlaceData, summaryOnly, limit, offset } = options;
    const params = new URLSearchParams();
    if (includePlaceData) params.set('includePlaceData', includePlaceData);
    if (summaryOnly) params.set('summaryOnly', 'true');
    if (limit !== undefined) params.set('limit', limit);
    if (offset !== undefined) params.set('offset', offset);
    const queryString = params.toString();
    return this.request(`/lists/user/${userId}${queryString ? `?${queryString}` : ''}`);
  }

  async getList(listId, options = {}) {
    // limit/offset enable two-phase loading: first page + itemsPagination
    // metadata, then remaining pages. Without them the full list is returned.
    const { limit, offset } = options;
    const params = new URLSearchParams();
    if (limit !== undefined) params.set('limit', limit);
    if (offset !== undefined) params.set('offset', offset);
    const queryString = params.toString();
    return this.request(`/lists/${listId}${queryString ? `?${queryString}` : ''}`);
  }

  async createList(listData) {
    return this.request('/lists', {
      method: 'POST',
      body: JSON.stringify(listData),
    });
  }

  async updateList(listId, updates) {
    return this.request(`/lists/${listId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  /**
   * Add a single place to a list
   * More efficient than updateList because it doesn't send the entire items array
   */
  async addPlaceToListOptimized(listId, place) {
    return this.request(`/lists/${listId}/places`, {
      method: 'POST',
      body: JSON.stringify(place),
    });
  }

  async updatePlaceInList(listId, placeId, updates) {
    return this.request(`/lists/${listId}/places/${placeId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async updatePlacePhoto(placeId, photoUrl) {
    return this.request(`/places/${placeId}/photo`, {
      method: 'PATCH',
      body: JSON.stringify({ photoUrl }),
    });
  }

  /**
   * Get permanent photo URL for a place from Firebase Storage
   * If photo doesn't exist, backend will fetch from Google and store it
   */
  async getPlacePhoto(placeId) {
    return this.request(`/places/${placeId}/photo`, {
      method: 'GET',
    });
  }

  async deleteList(listId) {
    return this.request(`/lists/${listId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Archive a list for the current user
   * The list will be hidden from the user's view but remain accessible to others
   */
  async archiveList(listId) {
    return this.request(`/lists/${listId}/archive`, {
      method: 'POST',
    });
  }

  /**
   * Unarchive (restore) a list for the current user
   */
  async unarchiveList(listId) {
    return this.request(`/lists/${listId}/archive`, {
      method: 'DELETE',
    });
  }

  /**
   * Get archived lists for the current user (both owned and collaborations)
   */
  async getArchivedLists() {
    return this.request('/lists/archived/me');
  }

  /**
   * Add a place to multiple lists in a single batch request
   * Much faster than calling updateList for each list separately
   */
  async batchAddPlaceToLists(place, listIds) {
    return this.request('/lists/batch/add-place', {
      method: 'POST',
      body: JSON.stringify({ place, listIds }),
    });
  }

  /**
   * Remove a place from multiple lists in a single batch request
   * Much faster than calling updateList for each list separately
   */
  async batchRemovePlaceFromLists(placeId, listIds) {
    return this.request('/lists/batch/remove-place', {
      method: 'POST',
      body: JSON.stringify({ placeId, listIds }),
    });
  }

  // Follow endpoints
  async followUser(targetUserId, options = {}) {
    return this.request(`/follows/${targetUserId}`, {
      method: 'POST',
      body: JSON.stringify(options),
    });
  }

  async unfollowUser(targetUserId) {
    return this.request(`/follows/${targetUserId}`, {
      method: 'DELETE',
    });
  }

  async getFollowers(userId) {
    return this.request(`/follows/${userId}/followers`);
  }

  async getFollowing(userId) {
    return this.request(`/follows/${userId}/following`);
  }

  // Check relationship status with a specific user (optimized for profile page)
  // Returns: isFollowing, isFriend, friendRequestSent, friendRequestReceived, incomingFriendRequestId
  async checkRelationshipStatus(userId) {
    return this.request(`/follows/${userId}/relationship`);
  }

  // Friend request endpoints
  async sendFriendRequest(toUserId) {
    return this.request('/friend-requests', {
      method: 'POST',
      body: JSON.stringify({ toUserId }),
    });
  }

  async respondToFriendRequest(requestId, accept, options = {}) {
    const action = accept ? 'accept' : 'reject';
    return this.request(`/friend-requests/${requestId}/${action}`, {
      method: 'POST',
      body: JSON.stringify(options),
    });
  }

  async getIncomingFriendRequests() {
    return this.request('/friend-requests/incoming');
  }

  async getArchivedFriendRequests() {
    return this.request('/friend-requests/incoming?includeHidden=true&hiddenOnly=true');
  }

  async getOutgoingFriendRequests() {
    return this.request('/friend-requests/outgoing');
  }

  async cancelFriendRequest(requestId) {
    return this.request(`/friend-requests/${requestId}`, {
      method: 'DELETE',
    });
  }

  async getIncomingRequestFromUser(fromUserId) {
    return this.request(`/friend-requests/from/${fromUserId}`);
  }

  async hideFriendRequest(requestId) {
    return this.request(`/friend-requests/${requestId}/hide`, {
      method: 'POST',
    });
  }

  // Friends endpoints
  async getFriends(userId) {
    return this.request(`/friends/${userId}`);
  }

  async unfriend(friendId) {
    return this.request(`/friends/${friendId}`, {
      method: 'DELETE',
    });
  }

  // Block endpoints
  async blockUser(targetUserId) {
    return this.request('/blocks', {
      method: 'POST',
      body: JSON.stringify({ userId: targetUserId }),
    });
  }

  async unblockUser(targetUserId) {
    return this.request(`/blocks/${targetUserId}`, {
      method: 'DELETE',
    });
  }

  // Discover endpoints
  async getDiscoverData(username, excludeUserId, limit = 10) {
    const params = new URLSearchParams();
    if (username) params.append('username', username);
    if (excludeUserId) params.append('exclude', excludeUserId);
    params.append('limit', limit);
    return this.request(`/lists/discover?${params.toString()}`);
  }

  // Static discover content - same for all users, can be cached aggressively
  async getDiscoverStatic(limit = 10) {
    return this.request(`/lists/discover/static?limit=${limit}`);
  }

  // Personalized discover content - varies by user
  async getDiscoverPersonalized(username, excludeUserId, limit = 10) {
    const params = new URLSearchParams();
    if (username) params.append('username', username);
    if (excludeUserId) params.append('exclude', excludeUserId);
    params.append('limit', limit);
    return this.request(`/lists/discover/personalized?${params.toString()}`);
  }

  async getFeaturedLists(limit = 10) {
    return this.request(`/lists/featured?limit=${limit}`);
  }

  async getPublicLists(excludeUsername, limit = 10) {
    return this.request(`/lists/public?exclude=${excludeUsername}&limit=${limit}`);
  }

  async getFriendsLists(username, limit = 10) {
    return this.request(`/lists/friends/${username}?limit=${limit}`);
  }

  async searchLists(query) {
    return this.request(`/lists/search?q=${encodeURIComponent(query)}`);
  }

  async searchUsers(query) {
    return this.request(`/users/search?q=${encodeURIComponent(query)}`);
  }

  // City Guides endpoints
  async getCityGuides() {
    return this.request('/city-guides');
  }

  async getCityGuide(slug, options = {}) {
    const params = new URLSearchParams();
    if (options.category) params.append('category', options.category);
    if (options.sort) params.append('sort', options.sort);
    const queryString = params.toString();
    return this.request(`/city-guides/${slug}${queryString ? `?${queryString}` : ''}`);
  }

  // List follow endpoints
  async followList(listId) {
    return this.request('/lists/follow', {
      method: 'POST',
      body: JSON.stringify({ listId }),
    });
  }

  async unfollowList(listId) {
    return this.request(`/lists/${listId}/follow`, {
      method: 'DELETE',
    });
  }

  async getFollowedLists(options = {}) {
    const { includePlaceData, summaryOnly, limit, offset } = options;
    const params = new URLSearchParams();
    if (includePlaceData) params.set('includePlaceData', includePlaceData);
    if (summaryOnly) params.set('summaryOnly', 'true');
    if (limit !== undefined) params.set('limit', limit);
    if (offset !== undefined) params.set('offset', offset);
    const queryString = params.toString();
    return this.request(`/lists/followed/me${queryString ? `?${queryString}` : ''}`);
  }

  async isFollowingList(listId) {
    return this.request(`/lists/${listId}/is-following`);
  }

  // Duplicate list endpoint
  async duplicateList(listId, options) {
    return this.request(`/lists/${listId}/duplicate`, {
      method: 'POST',
      body: JSON.stringify(options),
    });
  }

  // Import places from another list
  async importPlaces(destinationListId, sourceListId, placeIds = null) {
    return this.request(`/lists/${destinationListId}/import`, {
      method: 'POST',
      body: JSON.stringify({ sourceListId, placeIds }),
    });
  }

  // Get user's places by placeId (for checking if places exist on user's lists)
  async getUserPlacesByPlaceIds(placeIds) {
    return this.request('/lists/places/lookup', {
      method: 'POST',
      body: JSON.stringify({ placeIds }),
    });
  }

  // ==================== User Designations ====================

  // Get visited place IDs only (lightweight endpoint for % visited calculation)
  async getVisitedPlaceIds() {
    return this.request('/users/me/visited-place-ids');
  }

  // Get all user's designations
  async getUserDesignations() {
    return this.request('/users/me/designations');
  }

  // Get designation for a specific place
  async getDesignation(placeId) {
    return this.request(`/users/me/designations/${encodeURIComponent(placeId)}`);
  }

  // Set/update designation for a place
  async setDesignation(placeId, designationData) {
    return this.request(`/users/me/designations/${encodeURIComponent(placeId)}`, {
      method: 'PUT',
      body: JSON.stringify(designationData),
    });
  }

  // Delete designation for a place (full reset - removes from all lists)
  async deleteDesignation(placeId) {
    return this.request(`/users/me/designations/${encodeURIComponent(placeId)}`, {
      method: 'DELETE',
    });
  }

  // Update privacy setting for a designation
  async updateDesignationPrivacy(placeId, isPrivate) {
    return this.request(`/users/me/designations/${encodeURIComponent(placeId)}/privacy`, {
      method: 'PATCH',
      body: JSON.stringify({ isPrivate }),
    });
  }

  // Batch get designations for multiple places
  async getDesignationsBatch(placeIds) {
    return this.request('/users/me/designations/batch', {
      method: 'POST',
      body: JSON.stringify({ placeIds }),
    });
  }

  // Get collaborator notes for a place on a list
  async getCollaboratorNotes(listId, placeId) {
    return this.request(`/lists/${listId}/places/${encodeURIComponent(placeId)}/notes`);
  }

  // Get collaborator designations (badges) for a place on a list
  async getCollaboratorDesignations(listId, placeId) {
    return this.request(`/lists/${listId}/places/${encodeURIComponent(placeId)}/designations`);
  }

  // Get friends who have saved specific places (batch lookup)
  async getFriendsPlaces(placeIds) {
    return this.request('/lists/places/friends-lookup', {
      method: 'POST',
      body: JSON.stringify({ placeIds }),
    });
  }

  // Get a user's lists that contain a specific place
  async getUserListsWithPlace(userId, placeId) {
    return this.request(`/lists/user/${userId}/places?placeId=${encodeURIComponent(placeId)}`);
  }

  // Submit a content report
  async submitReport({ reportedUserId, contentType, placeId, content, comment }) {
    return this.request('/reports', {
      method: 'POST',
      body: JSON.stringify({ reportedUserId, contentType, placeId, content, comment }),
    });
  }

  // Activity notification endpoints
  async getActivityNotifications(limit = 50, unreadOnly = false) {
    return this.request(`/notifications?limit=${limit}&unreadOnly=${unreadOnly}`);
  }

  async getUnreadNotificationCount() {
    return this.request('/notifications/unread-count');
  }

  // Get all badge counts in a single call (optimized - single user doc read)
  async getBadgeCounts() {
    return this.request('/notifications/badge-counts');
  }

  // Sync badge counts from actual data (fixes drift)
  async syncBadgeCounts() {
    return this.request('/notifications/sync-counts', {
      method: 'POST',
    });
  }

  async markNotificationAsRead(notificationId) {
    return this.request(`/notifications/${notificationId}/read`, {
      method: 'POST',
    });
  }

  async markAllNotificationsAsRead() {
    return this.request('/notifications/mark-all-read', {
      method: 'POST',
    });
  }

  async deleteNotification(notificationId) {
    return this.request(`/notifications/${notificationId}`, {
      method: 'DELETE',
    });
  }

  async deleteAllNotifications() {
    return this.request('/notifications/all', {
      method: 'DELETE',
    });
  }

  // Notification preferences endpoints
  async getNotificationPreferences() {
    return this.request('/users/notification-preferences');
  }

  async updateNotificationPreferences(preferences) {
    return this.request('/users/notification-preferences', {
      method: 'PUT',
      body: JSON.stringify(preferences),
    });
  }

  // Toggle list-specific notification muting
  async toggleListNotifications(listId, muted) {
    return this.request(`/lists/${listId}/notifications`, {
      method: 'PUT',
      body: JSON.stringify({ muted }),
    });
  }

  // Public sharing endpoints (Premium)
  async generateShareLink(listId) {
    return this.request(`/lists/${listId}/share`, {
      method: 'POST',
    });
  }

  async revokeShareLink(listId) {
    return this.request(`/lists/${listId}/share`, {
      method: 'DELETE',
    });
  }

  async getSharedList(shareToken) {
    // This endpoint doesn't require auth - use fetch directly
    const url = `${this.baseUrl}/lists/shared/${shareToken}`;
    try {
      const response = await fetch(url, {
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'An error occurred');
      }
      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Places endpoints
  async getPlace(placeId) {
    return this.request(`/places/${encodeURIComponent(placeId)}`);
  }

  async getPlacesBatch(placeIds) {
    return this.request('/places/batch', {
      method: 'POST',
      body: JSON.stringify({ placeIds }),
    });
  }

  async refreshListPlaces(listId, openingHoursData) {
    return this.request(`/places/lists/${listId}/refresh`, {
      method: 'POST',
      body: JSON.stringify({ openingHoursData }),
    });
  }

  async checkRefreshEligibility(listId) {
    return this.request(`/places/lists/${listId}/refresh-eligibility`);
  }

  async checkPlaceRefreshEligibility(placeId) {
    return this.request(`/places/${placeId}/refresh-eligibility`);
  }

  // Payment endpoints
  async createCheckoutSession(plan) {
    return this.request('/payments/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify({ plan }),
    });
  }

  async createPortalSession() {
    return this.request('/payments/create-portal-session', {
      method: 'POST',
    });
  }

  async getSubscription() {
    return this.request('/payments/subscription');
  }

  async cancelSubscription() {
    return this.request('/payments/cancel', {
      method: 'POST',
    });
  }

  // Account deletion endpoint
  async deleteAccount() {
    return this.request('/users/account', {
      method: 'DELETE',
    });
  }

  // Collaborator endpoints
  async getCollaborationLists(options = {}) {
    const { includePlaceData, summaryOnly } = options;
    const params = new URLSearchParams();
    if (includePlaceData) params.set('includePlaceData', includePlaceData);
    if (summaryOnly) params.set('summaryOnly', 'true');
    const queryString = params.toString();
    return this.request(`/lists/collaborations/me${queryString ? `?${queryString}` : ''}`);
  }

  async getCollaborators(listId) {
    return this.request(`/lists/${listId}/collaborators`);
  }

  async addCollaborator(listId, collaboratorId) {
    return this.request(`/lists/${listId}/collaborators`, {
      method: 'POST',
      body: JSON.stringify({ collaboratorId }),
    });
  }

  async removeCollaborator(listId, collaboratorId) {
    return this.request(`/lists/${listId}/collaborators/${collaboratorId}`, {
      method: 'DELETE',
    });
  }

  async leaveCollaboration(listId) {
    return this.request(`/lists/${listId}/collaborators/leave`, {
      method: 'POST',
    });
  }

  async transferOwnership(listId, newOwnerId) {
    return this.request(`/lists/${listId}/transfer-ownership`, {
      method: 'POST',
      body: JSON.stringify({ newOwnerId }),
    });
  }

  // Place enrichment endpoints (Premium only - combined Enterprise call)
  async enrichPlace(placeId, forceRefresh = false) {
    return this.request(`/places/${placeId}/enrich`, {
      method: 'POST',
      body: JSON.stringify({ forceRefresh }),
    });
  }

  async batchEnrichPlaces(placeIds, forceRefresh = false) {
    return this.request('/places/enrich/batch', {
      method: 'POST',
      body: JSON.stringify({ placeIds, forceRefresh }),
    });
  }

  async checkEnrichmentNeeded(placeIds, maxAgeDays = 30) {
    return this.request('/places/enrich/check', {
      method: 'POST',
      body: JSON.stringify({ placeIds, maxAgeDays }),
    });
  }

  // V2 refresh - backend fetches all data with combined Enterprise call
  async refreshListPlacesV2(listId) {
    return this.request(`/places/lists/${listId}/refresh-v2`, {
      method: 'POST',
    });
  }

  async getGooglePlacesLogs(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/google-places/logs${queryString ? `?${queryString}` : ''}`);
  }

  async getGooglePlacesCosts(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/google-places/costs${queryString ? `?${queryString}` : ''}`);
  }

  // ==================== Admin Endpoints ====================

  // Admin - Dashboard
  async getAdminStats() {
    return this.request('/admin/stats');
  }

  // Admin - Featured Lists
  async getAdminFeaturedLists() {
    return this.request('/admin/featured-lists');
  }

  async searchListsForFeaturing(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/admin/lists/search${queryString ? `?${queryString}` : ''}`);
  }

  async setListFeatured(listId) {
    return this.request(`/admin/featured-lists/${listId}`, {
      method: 'POST',
    });
  }

  async removeListFeatured(listId) {
    return this.request(`/admin/featured-lists/${listId}`, {
      method: 'DELETE',
    });
  }

  // Admin - City Guides
  async getAdminCityGuides() {
    return this.request('/admin/city-guides');
  }

  async getAdminCityGuide(slug) {
    return this.request(`/admin/city-guides/${slug}`);
  }

  async createCityGuide(data) {
    return this.request('/admin/city-guides', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCityGuide(slug, updates) {
    return this.request(`/admin/city-guides/${slug}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteCityGuide(slug) {
    return this.request(`/admin/city-guides/${slug}`, {
      method: 'DELETE',
    });
  }

  async assignListToCityGuide(slug, listId, options = {}) {
    return this.request(`/admin/city-guides/${slug}/lists/${listId}`, {
      method: 'POST',
      body: JSON.stringify(options),
    });
  }

  async updateListInCityGuide(slug, listId, updates) {
    return this.request(`/admin/city-guides/${slug}/lists/${listId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async removeListFromCityGuide(slug, listId) {
    return this.request(`/admin/city-guides/${slug}/lists/${listId}`, {
      method: 'DELETE',
    });
  }

  // Admin - Bucket List Builders
  async getAdminBucketListBuilders() {
    return this.request('/admin/bucket-list-builders');
  }

  async addBucketListBuilder(listId, rank = null) {
    return this.request(`/admin/bucket-list-builders/${listId}`, {
      method: 'POST',
      body: JSON.stringify({ rank }),
    });
  }

  async updateBucketListBuilder(listId, updates) {
    return this.request(`/admin/bucket-list-builders/${listId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async removeBucketListBuilder(listId) {
    return this.request(`/admin/bucket-list-builders/${listId}`, {
      method: 'DELETE',
    });
  }

  // Admin - API Costs & Logs
  async getAdminApiCostStatus() {
    return this.request('/admin/api-costs/status');
  }

  async getAdminApiCostHistory(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/admin/api-costs/history${queryString ? `?${queryString}` : ''}`);
  }

  async getAdminHourlyCostHistory() {
    return this.request('/admin/api-costs/hourly');
  }

  async getAdminDailyCostHistory() {
    return this.request('/admin/api-costs/daily');
  }

  async getAdminCostAlerts() {
    return this.request('/admin/api-costs/alerts');
  }

  async acknowledgeAdminCostAlert(alertId) {
    return this.request(`/admin/api-costs/alerts/${alertId}/acknowledge`, {
      method: 'POST',
    });
  }

  async getAdminApiLogs(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/admin/api-logs${queryString ? `?${queryString}` : ''}`);
  }

  async getAdminApiLogsByUser(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/admin/api-logs/by-user${queryString ? `?${queryString}` : ''}`);
  }

  // Admin - Audit Logs
  async getAdminLogs(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/admin/logs${queryString ? `?${queryString}` : ''}`);
  }

  // Admin - User Stats
  async getAdminUserStats() {
    return this.request('/admin/users/stats');
  }

  async getAdminPremiumStats() {
    return this.request('/admin/users/premium-stats');
  }

  // Admin - Places Stats
  async getAdminPlacesStats() {
    return this.request('/admin/places/stats');
  }

  async getAdminPlacesStaleness() {
    return this.request('/admin/places/staleness');
  }

  // Admin - Referral/Viral Metrics
  async getAdminReferralMetrics() {
    return this.request('/admin/referrals/metrics');
  }

  // ==================== Referral Endpoints ====================

  /**
   * Get or create the current user's referral code
   */
  async getReferralCode() {
    return this.request('/referrals/code');
  }

  /**
   * Validate a referral code (public endpoint, works without auth)
   * @param {string} code - The referral code to validate
   */
  async validateReferralCode(code) {
    return this.request('/referrals/validate', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  /**
   * Apply a referral code to the current user
   * Grants 2-week free trial and increments referrer's count
   * @param {string} code - The referral code to apply
   */
  async applyReferralCode(code) {
    return this.request('/referrals/apply', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  /**
   * Get referral statistics for the current user
   * Returns referral count, credits earned/available, progress toward next reward
   */
  async getReferralStats() {
    return this.request('/referrals/stats');
  }

  /**
   * Redeem a referral credit for 1 month of premium
   */
  async redeemReferralCredit() {
    return this.request('/referrals/redeem', {
      method: 'POST',
    });
  }

  /**
   * Get referral history (list of people the user has referred)
   * @param {number} limit - Maximum number of referrals to return
   */
  async getReferralHistory(limit = 50) {
    return this.request(`/referrals/history?limit=${limit}`);
  }

  // ==================== Creator Endpoints ====================

  /**
   * Get creator status for the current user
   */
  async getCreatorStatus() {
    return this.request('/creators/status');
  }

  /**
   * Get creator referral stats (sign-ups and premium conversions)
   */
  async getCreatorStats() {
    return this.request('/creators/stats');
  }

  /**
   * Get creator's public lists for featured selection
   */
  async getCreatorLists() {
    return this.request('/creators/lists');
  }

  /**
   * Get creator referral link
   */
  async getCreatorReferralLink() {
    return this.request('/creators/referral-link');
  }

  /**
   * Set the creator's featured list
   * @param {string} listId - The list ID to feature
   */
  async setCreatorFeaturedList(listId) {
    return this.request('/creators/featured-list', {
      method: 'PUT',
      body: JSON.stringify({ listId }),
    });
  }

  /**
   * Remove the creator's featured list
   */
  async removeCreatorFeaturedList() {
    return this.request('/creators/featured-list', {
      method: 'DELETE',
    });
  }

  /**
   * Get public creator profile (no auth required)
   * @param {string} username - The creator's username
   */
  async getCreatorProfile(username) {
    return this.request(`/creators/profile/${username}`);
  }

  // ==================== Admin Creator Endpoints ====================

  /**
   * Get all creators (admin only)
   */
  async getAdminCreators() {
    return this.request('/admin/creators');
  }

  /**
   * Search users for creator selection (admin only)
   * @param {string} query - Search query
   */
  async searchUsersForCreator(query) {
    return this.request(`/admin/creators/search?q=${encodeURIComponent(query)}`);
  }

  /**
   * Grant creator status to a user (admin only)
   * @param {string} userId - The user ID
   * @param {object} options - Optional options (featuredListExpiresAt)
   */
  async addCreator(userId, options = {}) {
    return this.request(`/admin/creators/${userId}`, {
      method: 'POST',
      body: JSON.stringify(options),
    });
  }

  /**
   * Revoke creator status from a user (admin only)
   * @param {string} userId - The user ID
   */
  async removeCreator(userId) {
    return this.request(`/admin/creators/${userId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Update creator's featured list expiration date (admin only)
   * @param {string} userId - The user ID
   * @param {string} expirationDate - The new expiration date
   */
  async updateCreatorExpiration(userId, expirationDate) {
    return this.request(`/admin/creators/${userId}/expiration`, {
      method: 'PUT',
      body: JSON.stringify({ expirationDate }),
    });
  }

  /**
   * Toggle ambassador status for a creator (admin only)
   * @param {string} userId - The user ID
   */
  async toggleCreatorAmbassador(userId) {
    return this.request(`/admin/creators/${userId}/ambassador`, {
      method: 'PUT',
    });
  }

  // ==================== Manual Premium Management (Admin) ====================

  /**
   * Get all users with manual premium (admin only)
   */
  async getAdminManualPremiumUsers() {
    return this.request('/admin/manual-premium');
  }

  /**
   * Search users for manual premium selection (admin only)
   * @param {string} query - Search query
   */
  async searchUsersForManualPremium(query) {
    return this.request(`/admin/manual-premium/search?q=${encodeURIComponent(query)}`);
  }

  /**
   * Grant manual premium to a user (admin only)
   * @param {string} userId - The user ID
   */
  async grantManualPremium(userId) {
    return this.request(`/admin/manual-premium/${userId}`, {
      method: 'POST',
    });
  }

  /**
   * Revoke manual premium from a user (admin only)
   * @param {string} userId - The user ID
   */
  async revokeManualPremium(userId) {
    return this.request(`/admin/manual-premium/${userId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get pending reports (admin only)
   */
  async getAdminPendingReports() {
    return this.request('/reports/pending');
  }

  /**
   * Review a report (admin only)
   * @param {string} reportId - The report ID
   * @param {Object} params - { status: 'reviewed' | 'dismissed' | 'action_taken', reviewNotes?: string }
   */
  async reviewAdminReport(reportId, { status, reviewNotes }) {
    return this.request(`/reports/${reportId}`, {
      method: 'PATCH',
      body: JSON.stringify({ status, reviewNotes }),
    });
  }

  // ==================== Analytics Endpoints ====================

  /**
   * Track an analytics event (authenticated)
   * @param {Object} params
   * @param {string} params.eventType - Event type
   * @param {string} params.targetId - Target ID (listId, userId, etc.)
   * @param {string} params.targetType - Target type (list, profile, place)
   * @param {string} params.ownerId - Content owner's user ID
   * @param {Object} params.metadata - Additional metadata
   */
  async trackAnalyticsEvent({ eventType, targetId, targetType, ownerId, metadata }) {
    return this.request('/analytics/track', {
      method: 'POST',
      body: JSON.stringify({ eventType, targetId, targetType, ownerId, metadata }),
    });
  }

  /**
   * Track a public analytics event (anonymous, only for profile views)
   * @param {Object} params
   * @param {string} params.targetId - User ID of the profile owner
   * @param {string} params.ownerId - Same as targetId for profiles
   * @param {Object} params.metadata - Additional metadata (sessionId for deduplication)
   */
  async trackPublicProfileView({ targetId, ownerId, metadata }) {
    // Use fetch directly to avoid auth header for anonymous requests
    const url = `${this.baseUrl}/analytics/track-public`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        eventType: 'profile_view',
        targetId,
        targetType: 'profile',
        ownerId: ownerId || targetId,
        metadata,
      }),
    });
    return response.json();
  }

  /**
   * Get creator analytics
   * Returns aggregated stats with period comparisons
   */
  async getCreatorAnalytics() {
    return this.request('/analytics/creator');
  }

  // ==================== Collections Endpoints ====================

  /**
   * Get the current user's collections
   */
  async getMyCollections() {
    return this.request('/collections/me');
  }

  /**
   * Get a user's public collections (for profile view)
   * @param {string} userId - The user ID
   */
  async getUserCollections(userId) {
    return this.request(`/collections/user/${userId}`);
  }

  /**
   * Get a single collection by ID
   * @param {string} collectionId - The collection ID
   */
  async getCollection(collectionId) {
    return this.request(`/collections/${collectionId}`);
  }

  /**
   * Create a new collection
   * @param {Object} data - { title, visibility?, listIds? }
   */
  async createCollection(data) {
    return this.request('/collections', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update a collection
   * @param {string} collectionId - The collection ID
   * @param {Object} updates - Fields to update
   */
  async updateCollection(collectionId, updates) {
    return this.request(`/collections/${collectionId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  /**
   * Delete a collection
   * @param {string} collectionId - The collection ID
   */
  async deleteCollection(collectionId) {
    return this.request(`/collections/${collectionId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Reorder collections
   * @param {string[]} orderedIds - Array of collection IDs in new order
   */
  async reorderCollections(orderedIds) {
    return this.request('/collections/reorder', {
      method: 'PUT',
      body: JSON.stringify({ orderedIds }),
    });
  }

  /**
   * Add a list to one or more collections
   * @param {string} listId - The list ID
   * @param {string[]} collectionIds - Array of collection IDs
   */
  async addListToCollections(listId, collectionIds) {
    return this.request('/collections/add-list', {
      method: 'POST',
      body: JSON.stringify({ listId, collectionIds }),
    });
  }

  /**
   * Remove a list from a collection
   * @param {string} collectionId - The collection ID
   * @param {string} listId - The list ID
   */
  async removeListFromCollection(collectionId, listId) {
    return this.request(`/collections/${collectionId}/remove-list`, {
      method: 'POST',
      body: JSON.stringify({ listId }),
    });
  }

  /**
   * Get collections containing a specific list
   * @param {string} listId - The list ID
   */
  async getCollectionsForList(listId) {
    return this.request(`/collections/for-list/${listId}`);
  }
}

export default new ApiService();
